import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import OnboardingChecklistCard from '../components/OnboardingChecklistCard';
import FeatureNotEnabled from '../components/FeatureNotEnabled';
import { featureFlagsService } from '../services/api';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  User, 
  AlertCircle,
  FileText,
  CheckCircle,
  Clock,
  Phone,
  Mail,
  HelpCircle,
  ExternalLink
} from 'lucide-react';

/**
 * Driver Self-Service Dashboard (Phase 6 - Scaffolded)
 * 
 * FEATURE FLAG: driver_dashboard_enabled
 * ACCESS: Driver role (when enabled)
 * STATUS: Placeholder - View-only, NO edits, NO load acceptance, NO settlement data
 * 
 * IMPORTANT: This is extremely limited even when enabled:
 * - Application Status (read-only)
 * - Onboarding Status (read-only)
 * - Contact Support link only
 */
const DriverDashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [featureEnabled, setFeatureEnabled] = useState(false);
  const [loading, setLoading] = useState(true);

  // This page is for drivers when the feature is enabled
  // Super Admins can also view it for testing
  const allowedRoles = ['driver', 'super_admin', 'admin'];

  useEffect(() => {
    if (user && !allowedRoles.includes(user.role)) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  useEffect(() => {
    const checkFeature = async () => {
      try {
        const enabledFeatures = await featureFlagsService.getEnabledFeatures();
        setFeatureEnabled(enabledFeatures.includes('driver_dashboard_enabled'));
      } catch (err) {
        console.error('Failed to check feature flag:', err);
        setFeatureEnabled(false);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      checkFeature();
    }
  }, [token]);

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </DashboardLayout>
    );
  }

  if (!featureEnabled) {
    return <FeatureNotEnabled featureName="Driver Self-Service Dashboard" />;
  }

  // Placeholder content when feature IS enabled (future-ready scaffold)
  // This is VIEW-ONLY - no edits allowed
  return (
    <DashboardLayout>
      <div className="max-w-4xl mx-auto p-4 md:p-6">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900 flex items-center gap-2">
            <User className="w-8 h-8 text-blue-600" />
            Driver Dashboard
          </h1>
          <p className="text-gray-600 mt-1">View your application and onboarding status</p>
        </div>

        {/* View-Only Notice */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <div className="font-semibold text-blue-800">View-Only Access</div>
            <div className="text-sm text-blue-700">
              This dashboard shows your current status. To make changes or updates, please contact support.
            </div>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid md:grid-cols-2 gap-6 mb-6">
          {/* Application Status (Read-Only) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <FileText className="w-5 h-5 text-blue-600" />
                Application Status
              </CardTitle>
              <CardDescription>Your driver application status</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Submission Date</span>
                  <span className="text-gray-400 font-mono text-sm">--/--/----</span>
                </div>
                <div className="flex items-center justify-between py-2 border-b border-gray-100">
                  <span className="text-gray-600">Current Status</span>
                  <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-600">
                    <Clock className="w-3 h-3 mr-1" />
                    Pending
                  </span>
                </div>
                <div className="flex items-center justify-between py-2">
                  <span className="text-gray-600">Application ID</span>
                  <span className="text-gray-400 font-mono text-sm">--------</span>
                </div>
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Status updates are made by administrators. Check back for updates.
              </p>
            </CardContent>
          </Card>

          {/* Onboarding Status (Read-Only) */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="w-5 h-5 text-green-600" />
                Onboarding Status
              </CardTitle>
              <CardDescription>Your onboarding progress</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {[
                  { label: 'Application Submitted', status: 'pending' },
                  { label: 'Documents Uploaded', status: 'pending' },
                  { label: 'Background Check', status: 'pending' },
                  { label: 'Training Complete', status: 'pending' },
                  { label: 'Ready for Dispatch', status: 'pending' }
                ].map((step, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full bg-gray-200 flex items-center justify-center">
                      <span className="text-xs text-gray-400">{index + 1}</span>
                    </div>
                    <span className="text-gray-400 flex-1">{step.label}</span>
                    <Clock className="w-4 h-4 text-gray-300" />
                  </div>
                ))}
              </div>
              <p className="text-xs text-gray-400 mt-4">
                Progress is updated as you complete each step.
              </p>
            </CardContent>
          </Card>

          <OnboardingChecklistCard />
        </div>

        {/* Contact Support Section */}
        <Card className="border-blue-200 bg-blue-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-blue-600" />
              Need Help?
            </CardTitle>
            <CardDescription>Contact our support team for assistance</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link to="/contact" className="flex-1">
                <Button className="w-full flex items-center justify-center gap-2" data-testid="contact-support-btn">
                  <Mail className="w-4 h-4" />
                  Contact Support
                  <ExternalLink className="w-4 h-4" />
                </Button>
              </Link>
              <div className="flex items-center gap-2 text-gray-600">
                <Phone className="w-4 h-4" />
                <span className="text-sm">Or call: (616) 555-BLUE</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Limitations Notice */}
        <div className="mt-6 p-4 bg-gray-100 rounded-lg">
          <p className="text-sm text-gray-500 text-center">
            <strong>Note:</strong> This dashboard is for status viewing only. 
            Load acceptance, settlement information, and profile editing are not available in this version.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DriverDashboardPage;
